/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TimezoneService } from '../../pages/resources/equipment/services/timezones.service';
import { OspDateTime } from '../../shared/contract/osp-date-time.contract';
import { DatesService } from '../../shared/services/dates.service';

@Injectable()
export class TimezoneInterceptor implements HttpInterceptor {
  private timezoneService = inject(TimezoneService);
  private timezonesDiffMinutes = 0;
  private localTimezone: string = 'UTC';
  private userTimezone: string = 'UTC';

  // TODO: Temporary logging
  private static readonly storageKeyTZ = 'TIMEZONE_INTERCEPTOR_LOGS_ENABLED';
  private static ENABLE_LOGS = /^true$/i.test(localStorage.getItem(TimezoneInterceptor.storageKeyTZ) || '');

  static isLogsEnabled(): boolean {
    return TimezoneInterceptor.ENABLE_LOGS;
  }
  static toggleLogs(enable: boolean): void {
    this.ENABLE_LOGS = enable;
    localStorage.setItem(TimezoneInterceptor.storageKeyTZ, String(enable))
  }
  private consoleLog(...texts: string[]): void {
    if (TimezoneInterceptor.ENABLE_LOGS) {
      console.log(...texts);
    }
  }
  private consoleGroup(...texts: string[]): void {
    if (TimezoneInterceptor.ENABLE_LOGS) {
      console.group(...texts);
    }
  }
  private consoleGroupEnd(): void {
    if (TimezoneInterceptor.ENABLE_LOGS) {
      console.groupEnd();
    }
  }

  constructor() {
    this.initializeConfigs();
  }

  private initializeConfigs(): void {
    this.localTimezone = DatesService.getLocalTimeZone();
    this.userTimezone = this.timezoneService.getUserTimezone();
    this.timezonesDiffMinutes = this.getTimezonesDiffMinutes();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.timezonesDiffMinutes === 0) {
      // A timezone the same. Do nothing..
      return next.handle(req);
    }

    this.consoleLog('========== Timezone interceptor START ==========');
    this.consoleLog('URL: ', req.url);

    this.consoleGroup('REQUEST PARAMS');
    const params = this.handleRequestParams(req.params);
    this.consoleGroupEnd();

    this.consoleGroup('REQUEST BODY');
    const body = this.handleRequestBody(req.body);
    this.consoleGroupEnd();

    const modifiedRequest = req.clone({ body, params });

    // Pass the modified request and transform the response body
    return next.handle(modifiedRequest).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          this.consoleGroup('RESPONSE BODY');
          const body = this.handleResponseBody(event.body);
          this.consoleGroupEnd();
          this.consoleLog('========== Timezone interceptor END ==========');
          return event.clone({ body });
        }
        return event;
      }),
    );
  }

  private handleRequestParams(params: HttpParams): HttpParams {
    let convertedParams = new HttpParams();

    params.keys().forEach((key) => {
      const values = params.getAll(key);
      if (values) {
        values.forEach((value) => {
          const converted = this.convertDates(value, false, key);
          convertedParams = convertedParams.append(key, converted);
        });
      }
    });

    return convertedParams;
  }


  private handleRequestBody(payload: any): any {
    return this.convertDates(payload, false, 'body');
  }

  private handleResponseBody(payload: any): any {
    return this.convertDates(payload, true, 'body');
  }

  private convertDates(payload: any, isInput: boolean, propertyName: string): any {
    if (isNil(payload) || isEmpty(payload)) {
      return payload;
    }

    if (typeof payload === 'string' && this.isDate(payload)) {
      // Date conversion
      const converted = this.shiftDate(payload, isInput);
      this.consoleLog(`CONVERSION [${propertyName}]: ${payload} --> ${converted}`)
      return converted;
    }

    if (this.isOspDateTime(payload)) {
      return this.shiftOspDateTime(payload, isInput);
    }

    if (typeof payload === 'string') {
      const t = payload.trim();
      if (t.startsWith('{') || t.startsWith('[')) {
        try {
          const parsed = JSON.parse(t);
          const converted = this.convertDates(parsed, isInput, propertyName);
          return JSON.stringify(converted);
        } catch {
          return payload;
        }
      }
      return payload;
    }

    if (Array.isArray(payload)) {
      return payload.map((item) => this.convertDates(item, isInput, propertyName));
    }

    return Object.keys(payload).reduce((acc: any, key: string) => {
      acc[key] = this.convertDates(payload[key], isInput, key);
      return acc;
    }, {});
  }

  private isDate(value: string): boolean {
    // Prevent from wrongly detecting non-date strings as valid (i.e: postal codes)
    if (!value || value.length < 10 || value.length > 30 || value.indexOf('T') === -1) {
      return false;
    }

    const parsedDate = moment(value, moment.ISO_8601, true);
    return parsedDate.isValid();
  }

  private isOspDateTime(value: any): value is OspDateTime {
    return Boolean(
      value
      && (value as OspDateTime).timestamp
      && (value as OspDateTime).zoneId
    );
  }

  // Calculation of the timeshift between machine and user's timezones
  private getTimezonesDiffMinutes(): number {
    const actualOffset = moment().utcOffset();
    const zonedOffset = moment().tz(this.timezoneService.getUserTimezone()).utcOffset();
    return zonedOffset - actualOffset;
  }

  private shiftDate(date: Date | string, isInput: boolean): string {
    const offset = (isInput ? 1 : -1) * this.timezonesDiffMinutes;
    return moment(date).add(offset, 'minutes').toISOString();
  }

  private shiftOspDateTime(value: OspDateTime, isInput: boolean): OspDateTime {
    return {
      timestamp: this.shiftDate(value.timestamp, isInput),
      zoneId: isInput ? this.localTimezone : this.userTimezone,
    }
  }
}
